import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { getWeekNumber } from '../../libs/dateTimeUtilities';
import Button from '../Button/Button';
import { StyledWeekNavigation, StyledWeekNavigationButton } from './WeekNavigation.style';
import { Meta } from '../../styled components/Typography';
import debounce from '../../libs/debounce';

const WeekNavigation = ({
  currentWeek,
  goToNextWeek,
  goToPreviousWeek,
  onWeekNameClick = false,
  datepickerIsOpen = false
}) => {
  /* We only debounce the listener for clicking on the week name here,
    bacause the actions of going to the next or previous week cause a complete unmounting and reconstructing of the container components
    (EventsContainer and RosterContainer), causing debounced methods to be recreated and thus not performing an actual rebounce.
    Because this component stays on the page when clicking, a debounce is necessary because the user can keep on clicking and navigating to other weeks.
    The rebouncing for the next/previous week navigation is handled in the DateNavigationUrlWrapper and NavigationHistory wrapper */
  let debouncedOnWeekNameClick = null;
  if (onWeekNameClick)
    debouncedOnWeekNameClick = useMemo(() => debounce(onWeekNameClick), []);
  return (
    <StyledWeekNavigation
      data-test-events-weeknavigation
      $datepickerIsOpen={datepickerIsOpen}
    >
      <StyledWeekNavigationButton
        icon="navigate-before"
        onClick={goToPreviousWeek}
        $datepickerIsOpen={datepickerIsOpen}
        dataAttributes={{
          'data-test-button': 'navigatePreviousWeek'
        }}
      />

      {onWeekNameClick ? (
        <Button
          color={datepickerIsOpen ? 'primary' : 'default'}
          contentType="toggle"
          icon="calendar_month"
          iconPosition="right"
          tw="tabular-nums"
          onClick={debouncedOnWeekNameClick}
          dataAttributes={{
            'data-test-button': 'weekNameButton'
          }}
        >
          {`${i18n('Week')} ${getWeekNumber(currentWeek)}`}
        </Button>
      ) : (
        <Meta $color="gray dark" tw="tabular-nums px-8 py-4 font-medium leading-normal">
          {`${i18n('Week')} ${getWeekNumber(currentWeek)}`}
        </Meta>
      )}

      <StyledWeekNavigationButton
        icon="navigate-next"
        onClick={goToNextWeek}
        $datepickerIsOpen={datepickerIsOpen}
        dataAttributes={{
          'data-test-button': 'navigateNextWeek'
        }}
      />
    </StyledWeekNavigation>
  );
};

WeekNavigation.propTypes = {
  currentWeek: PropTypes.shape({
    startDateObject: PropTypes.shape({}),
    endDateObject: PropTypes.shape({})
  }).isRequired,
  goToPreviousWeek: PropTypes.func.isRequired,
  goToNextWeek: PropTypes.func.isRequired,
  onWeekNameClick: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  datepickerIsOpen: PropTypes.bool
};

export default WeekNavigation;
